/** Server-backed pickers for creators, collections and programmes (search + paging). */
import { useState } from 'react';
import { Group, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useCollectionList, useCreator, useCreatorList, useProgrammeList } from '@/api/queries';
import type { AdminCollection, AdminCreator, ProgrammeListItem } from '@/api/types';
import { SEARCH_DEBOUNCE_MS } from '@/lib/useListUrlState';
import { ServerSelect, type ServerSelectProps } from './ServerSelect';
import { StatusBadge } from './StatusBadge';

type PassThrough = Pick<
  ServerSelectProps<unknown>,
  | 'label'
  | 'aria-label'
  | 'description'
  | 'placeholder'
  | 'required'
  | 'clearable'
  | 'disabled'
  | 'error'
  | 'size'
  | 'w'
  | 'nothingFound'
>;

/** Search text plus its debounced value, and whether the dropdown is open. */
function usePickerSearch() {
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [opened, setOpened] = useState(false);
  return { search, setSearch, q: debounced || undefined, opened, setOpened };
}

export function CreatorSelect({
  value,
  onChange,
  selected,
  ...rest
}: PassThrough & {
  value: string | null;
  onChange: (id: string | null, creator: AdminCreator | null) => void;
  /** The creator already loaded with the entity (e.g. `programme.creator`), for its label. */
  selected?: { id: string; name: string } | null;
}) {
  const s = usePickerSearch();
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const list = useCreatorList({ q: s.q }, { enabled: s.opened });
  const known = [selected, picked].find((c) => c && c.id === value)?.name;
  // Only fetch the one creator when its name isn't known any other way (e.g. an id from the URL).
  const single = useCreator(value && !known ? value : null);
  return (
    <ServerSelect
      {...rest}
      value={value}
      valueLabel={known ?? single.data?.name ?? null}
      onChange={(id, c) => {
        if (c) setPicked({ id: c.id, name: c.name });
        onChange(id, c);
      }}
      search={s.search}
      onSearchChange={s.setSearch}
      onOpenChange={s.setOpened}
      query={list}
      getValue={(c) => c.id}
      getLabel={(c) => c.name}
      noun="creators"
    />
  );
}

export function CollectionSelect({
  value,
  onChange,
  valueLabel,
  exclude,
  ...rest
}: PassThrough & {
  value: string | null;
  onChange: (id: string | null, collection: AdminCollection | null) => void;
  valueLabel?: string | null;
  exclude?: (c: AdminCollection) => boolean;
}) {
  const s = usePickerSearch();
  const list = useCollectionList({ q: s.q }, { enabled: s.opened });
  return (
    <ServerSelect
      {...rest}
      value={value}
      valueLabel={valueLabel}
      onChange={onChange}
      search={s.search}
      onSearchChange={s.setSearch}
      onOpenChange={s.setOpened}
      query={list}
      getValue={(c) => c.id}
      getLabel={(c) => c.title}
      exclude={exclude}
      noun="collections"
    />
  );
}

/** Pick a programme to add somewhere; the input empties again after each pick. */
export function ProgrammeAddSelect({
  onAdd,
  exclude,
  ...rest
}: PassThrough & {
  onAdd: (programme: ProgrammeListItem) => void;
  exclude?: (p: ProgrammeListItem) => boolean;
}) {
  const s = usePickerSearch();
  const list = useProgrammeList({ q: s.q }, { enabled: s.opened });
  return (
    <ServerSelect
      {...rest}
      value={null}
      onChange={(_, p) => p && onAdd(p)}
      search={s.search}
      onSearchChange={s.setSearch}
      onOpenChange={s.setOpened}
      query={list}
      getValue={(p) => p.id}
      getLabel={(p) => p.title}
      renderOption={(p) => (
        <Group gap="xs" wrap="nowrap" justify="space-between">
          <Text size="sm" truncate>
            {p.title}
          </Text>
          <StatusBadge status={p.status} scheduled={p.scheduled} />
        </Group>
      )}
      exclude={exclude}
      noun="programmes"
    />
  );
}
